import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useSpring } from "framer-motion";
import { api } from "../lib/api";
import { Reveal, useSeo } from "../lib/ui";
import { StageBar, StageClose } from "../components/StageChain";
import { MissingPlayer } from "../components/MissingPlayer";

const Entry = ({ item, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 26 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-70px" }}
    transition={{ duration: 0.6, delay: (index % 3) * 0.06, ease: [0.16, 1, 0.3, 1] }}
    className="relative grid gap-4 border-b border-white/10 py-10 pl-10 md:grid-cols-[140px_minmax(0,1fr)] md:gap-10 md:pl-16"
    data-testid={`first-${index}`}
  >
    <span className="absolute left-[13px] top-[52px] h-3 w-3 -translate-x-1/2 bg-maasai md:left-[25px]" aria-hidden="true" />
    <div>
      <p className="font-display text-3xl font-bold leading-none text-ochre md:text-4xl">{item.year}</p>
      <p className="mt-2 text-[0.6rem] font-bold uppercase tracking-[0.2em] text-zinc-500">{item.category}</p>
    </div>
    <div>
      <h2 className="font-display text-xl uppercase leading-tight tracking-tight text-white md:text-3xl">
        {item.title}
      </h2>
      <p className="body-copy mt-4">{item.text}</p>
      {item.player ? (
        item.slug ? (
          <Link
            to={`/hall-of-fame/${item.slug}`}
            data-testid={`first-player-link-${index}`}
            className="mt-5 inline-block text-[0.66rem] font-bold uppercase tracking-[0.18em] text-ochre underline decoration-maasai underline-offset-4 hover:text-white"
          >
            {item.player} — read the profile
          </Link>
        ) : (
          <p className="mt-5 text-[0.66rem] font-bold uppercase tracking-[0.18em] text-zinc-500">{item.player}</p>
        )
      ) : null}
    </div>
  </motion.div>
);

export default function Firsts() {
  const [items, setItems] = useState([]);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 80%", "end 60%"] });
  const drawn = useSpring(scrollYProgress, { stiffness: 60, damping: 22 });

  useSeo({
    title: "First To Do It",
    description:
      "Every Kenyan football first, from the first Kenyan to sign for an English professional club at Walsall in 2001 to Champions League medals, Scottish cups and the J.League Golden Boot.",
  });

  useEffect(() => {
    api.get("/firsts").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  return (
    <div className="bg-black pb-24 pt-12 md:pt-16" data-testid="firsts-page">
      <StageBar stage="history" />
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        <Reveal>
          <p className="eyebrow">First to do it</p>
          <h1 className="display-1 mt-5 max-w-4xl">First To Do It</h1>
          <p className="body-copy mt-6 max-w-3xl">
            From a teenager signing at Walsall in 2001 to a Champions League medal in Milan, a first goal in the
            competition at Celtic Park and a Golden Boot in Japan. Every entry below is a first, a title or a landmark
            achieved by a Kenyan or a player of Kenyan descent — evidence that the talent has always exported itself.
            Each one was reached without a national development system behind it. That is the case for building one.
          </p>
          <div className="mono mt-8 flex flex-wrap gap-8 border-t border-white/10 pt-6 text-sm">
            <span data-testid="firsts-count">
              <span className="text-ochre">{items.length}</span> landmarks
            </span>
            <Link to="/womens-game" className="text-zinc-400 transition-colors hover:text-ochre" data-testid="firsts-womens-cta">
              The women&rsquo;s game &mdash; firsts, professionals and officials &rarr;
            </Link>
          </div>
        </Reveal>

        <div ref={ref} className="relative mt-14">
          <div className="absolute bottom-0 left-[13px] top-0 w-px bg-white/10 md:left-[25px]" aria-hidden="true" />
          <motion.div
            className="absolute left-[13px] top-0 w-px origin-top bg-maasai md:left-[25px]"
            style={{ scaleY: drawn, height: "100%" }}
            aria-hidden="true"
            data-testid="firsts-timeline-line"
          />
          {items.map((item, i) => (
            <Entry key={`${item.year}-${i}`} item={item} index={i} />
          ))}
        </div>
      </div>
      <div className="mt-16">
        <MissingPlayer context="first" />
      </div>
      <StageClose stage="history" />
    </div>
  );
}
